import { NotificationTypes, type SafeAny } from '@/common/types';
import {
  axiosInstance,
  addToQueue,
  setQueueKeyAlias,
  isEventValid,
  setRequestUpdateRequired,
  isRequestUpdateRequired
} from '@/common/utils';

const insertEventInSignalEvents = (
  commit: SafeAny,
  eventData: { id: string; type: string; start: string; duration: number }
) => {
  commit('insertEventInSignalEvents', eventData);
};

export const insertStudyScoreSetEvent = async (
  { state, rootState, commit, dispatch }: SafeAny,
  {
    id,
    studyId,
    type,
    start,
    duration,
    epochSize = 30
  }: {
    id: string;
    studyId: string;
    type: string;
    start: string;
    duration: number;
    epochSize?: number;
  }
) => {
  // TODO: Centralize these validations(same for update-event).
  const validationMessage = isEventValid(
    type,
    start,
    duration,
    rootState.scoreSet.scoreSet.data,
    state.signalEvents
  );

  if (validationMessage) {
    commit(
      'addNotification',
      {
        i18nTitle: 'errorAddScoreSetEvent',
        i18nDescription: validationMessage.key,
        i18nParams: validationMessage.params,
        type: validationMessage.type
      },
      { root: true }
    );
    state.eventActions.error = undefined;
    return false;
  }

  insertEventInSignalEvents(commit, { id, type, start, duration });

  const executeRequest = async () => {
    const url = `${rootState.env?.requestUrl.events}/studies/${studyId}/events`;

    try {
      if (isRequestUpdateRequired) {
        const eventAlreadyExists = Object.values(state.signalEvents).some((eventsArray: SafeAny) =>
          eventsArray?.some((e: SafeAny) => e.id === id)
        );

        if (!eventAlreadyExists) {
          insertEventInSignalEvents(commit, { id, type, start, duration });
        }
      }

      const res = await axiosInstance.post<SafeAny>(url, [
        {
          type,
          start,
          duration
        }
      ]);

      if (res?.status === 200) {
        const actualEventID = res?.data?.length > 0 ? res?.data[0] : '';

        if (actualEventID) {
          setQueueKeyAlias(`event:${studyId}:${actualEventID}`, `event:${studyId}:${id}`);
        }

        commit('updateSignalEventWithActualID', {
          tempEventID: id,
          actualEventID: actualEventID,
          type: type
        });
      } else if (res?.status === 205) {
        setRequestUpdateRequired(true);
        await dispatch('scoreSet/getScoreSetByStudy', { sleepStudyID: studyId }, { root: true });
        await dispatch('epoch/getStudyScoreSetEvents', { studyId, epochSize }, { root: true });
      } else {
        commit('removeEventInSignalEvents', { id: id, type });
        commit(
          'addNotification',
          {
            i18nTitle: 'errorAddScoreSetEvent',
            i18nDescription: `Unexpected status code: ${res.status}`,
            type: NotificationTypes.Error
          },
          { root: true }
        );
      }
    } catch (error: SafeAny) {
      commit('removeEventInSignalEvents', { id: id, type });
      state.eventActions.errorTimestamp = new Date().getTime();

      commit(
        'addNotification',
        {
          i18nTitle: 'errorAddScoreSetEvent',
          i18nDescription: error.message,
          type: NotificationTypes.Error
        },
        { root: true }
      );
    }
  };

  addToQueue(
    executeRequest,
    { originalEvent: {}, url: '' },
    () => {
      commit(
        'addNotification',
        {
          i18nTitle: 'warningEventLimit',
          i18nDescription: 'warningEventLimitDescription',
          type: NotificationTypes.Warning
        },
        { root: true }
      );
    },
    {
      key: `event-insert:${studyId}:${id}`,
      strategy: 'replace'
    }
  );

  return true;
};
